import React, { useMemo, useState } from 'react';
import type { HourEntry, HourForecast, Invoice, ProjectPhase } from '../../../../shared/types';
import type { Project, ProjectBudget } from '../../../../shared/entities';
import { TimeService, addDays, mondayOf } from '../../../services/timeService';
import { ProjectBudgetService } from '../../../services/projectBudgetService';
import { InvoiceService } from '../../../services/invoiceService';
import {
  WidgetFrame,
  Stat,
  StatGroup,
  Meter,
  WChart,
  WList,
  Fields,
  type WListRow,
  type FieldDef,
} from '../../../components/widgets';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { cn } from '../../../lib/utils';
import {
  fmtHours,
  fmtMoney,
  fmtPct,
  todayIso,
  shortWeek,
  projectMemberRate,
  btnGhost,
  linkBtn,
  inputStyle,
  sectionLabel,
  useSettledFetch,
} from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';
import type { WidgetSlot } from '../../../components/WidgetGrid';

// ═══════════════════════════════════════════════════════════════
// BUDGET (Widgets R2 T2b — merge of budget_hours + budget_dollars +
// burn_rate + variance + Profitability) — hard-gated by
// projects.view_financials. Hours / Dollars toggle is remembered per
// widget via `slot.settings.mode` (persists with the layout). The one
// real write path (inline "Set/Edit budget") survives Hours-only,
// exactly as budget_hours had it; Dollars keeps its original "Open
// budget editor" drill-only path. Per-phase rows and the burn chart now
// render at EVERY width (Decision 1 — the old width-tier gate is
// gone); the Profitability section (every function from the deleted
// profitability-widget.tsx) always renders below the budget summary,
// independent of whether a budget target is set, since it never
// depended on Budget's own empty state.
// ═══════════════════════════════════════════════════════════════

type Mode = 'hours' | 'dollars';

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div
      role="group"
      aria-label="Units"
      className="inline-flex flex-shrink-0 rounded-md border border-border p-0.5"
    >
      {(['hours', 'dollars'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          aria-pressed={mode === m}
          className={cn(
            'rounded px-2 text-xs font-semibold transition-colors',
            mode === m ? 'bg-hover text-text' : 'text-muted hover:text-text'
          )}
          style={{ minHeight: rem(32) }}
        >
          {m === 'hours' ? 'Hours' : 'Dollars'}
        </button>
      ))}
    </div>
  );
}

function BudgetBody({ ctx, slot }: { ctx: ProjectWidgetCtx; slot: WidgetSlot }) {
  const mode: Mode = slot.settings.mode === 'dollars' ? 'dollars' : 'hours';
  const setMode = (m: Mode) => slot.setSettings({ mode: m });

  // R2-T2 — Contract value + Fee structure moved from the project panel's
  // Details section into Budget (Josh: money belongs where it's already
  // permission-gated, not on a surface every team member sees). Editor
  // kind + save path copied verbatim from ProjectPanel's DetailsSection.
  const p = ctx.project;
  const saveProjectPatch = (patch: Partial<Project>) =>
    ctx.saveProject({ ...p, ...patch }).then(() => undefined);
  const fee = ctx.billingTypes.find((t) => t.id === p.billingTypeId)?.name || '–';
  const budgetFields: FieldDef[] = [
    {
      key: 'contractValue',
      label: 'Contract value',
      value: p.contractValue != null ? String(p.contractValue) : null,
      display: p.contractValue != null ? `$${p.contractValue.toLocaleString()}` : '–',
      editor: { kind: 'number' },
      canEdit: ctx.can.edit,
      onSave: (v: string) => saveProjectPatch({ contractValue: v ? Number(v) : undefined }),
    },
    {
      key: 'fee',
      label: 'Fee structure',
      value: p.billingTypeId || null,
      display: fee,
      editor: {
        kind: 'select',
        options: ctx.billingTypes.map((t) => ({ value: t.id, label: t.name })),
      },
      canEdit: ctx.can.edit,
      onSave: (v) => saveProjectPatch({ billingTypeId: v || undefined }),
    },
  ];

  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  // Profitability fold-in (deleted profitability-widget.tsx) — invoices are
  // the one new data source; `.catch(() => [])` preserved verbatim from
  // that file so a failed invoice load never blanks the Budget summary.
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const today = useMemo(() => todayIso(), []);
  const past4Start = useMemo(() => addDays(mondayOf(today), -7 * 4), [today]);
  const past4End = useMemo(() => addDays(mondayOf(today), -1), [today]);

  const { status, reload } = useSettledFetch(
    () => [
      ProjectBudgetService.listForProject(ctx.project.id),
      TimeService.listEntries(ctx.project.id),
      TimeService.syncPhasesWithDefaults(ctx.project.id),
      TimeService.listForecastsForRange(past4Start, past4End),
      InvoiceService.list(ctx.project.id).catch(() => [] as Invoice[]),
    ],
    (results) => {
      const [bR, eR, pR, fR, invR] = results;
      if (bR.status === 'fulfilled') setBudgets(bR.value);
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (pR.status === 'fulfilled') setPhases(pR.value);
      if (fR.status === 'fulfilled')
        setForecasts(fR.value.filter((f: HourForecast) => f.projectId === ctx.project.id));
      if (invR.status === 'fulfilled') setInvoices(invR.value);
    },
    [ctx.project.id, past4Start, past4End]
  );

  const totalBudgetHours = useMemo(() => {
    const t = budgets.find((b) => !b.phaseId && !b.billingRoleId);
    if (t?.budgetHours) return t.budgetHours;
    return ProjectBudgetService.computeRollup(budgets).hours;
  }, [budgets]);

  const totalBudgetDollars = useMemo(() => {
    const t = budgets.find((b) => !b.phaseId && !b.billingRoleId);
    if (t?.budgetAmount) return t.budgetAmount;
    return ProjectBudgetService.computeRollup(budgets).amount;
  }, [budgets]);

  const actualHours = useMemo(
    () => entries.reduce((s, e) => s + (Number(e.hours) || 0), 0),
    [entries]
  );
  const actualDollars = useMemo(() => {
    let sum = 0;
    for (const e of entries) {
      const m = ctx.members.find((x) => x.id === e.teamMemberId);
      sum += (Number(e.hours) || 0) * projectMemberRate(m, ctx.billingRoles);
    }
    return sum;
  }, [entries, ctx.members, ctx.billingRoles]);

  const totalBudget = mode === 'hours' ? totalBudgetHours : totalBudgetDollars;
  const actual = mode === 'hours' ? actualHours : actualDollars;
  const fmt = mode === 'hours' ? fmtHours : fmtMoney;
  const remaining = Math.max(0, totalBudget - actual);

  // Per-phase rows — every width (Decision 1 — the width-tier gate is gone).
  const phaseRows = useMemo(() => {
    const byPhaseHours = new Map<string, number>();
    const byPhaseDollars = new Map<string, number>();
    for (const e of entries) {
      const k = e.phaseId || '';
      const h = Number(e.hours) || 0;
      const m = ctx.members.find((x) => x.id === e.teamMemberId);
      byPhaseHours.set(k, (byPhaseHours.get(k) || 0) + h);
      byPhaseDollars.set(
        k,
        (byPhaseDollars.get(k) || 0) + h * projectMemberRate(m, ctx.billingRoles)
      );
    }
    return phases
      .map((p) => {
        const b = budgets.find((x) => x.phaseId === p.id && !x.billingRoleId);
        const budget = mode === 'hours' ? b?.budgetHours || 0 : b?.budgetAmount || 0;
        const value =
          mode === 'hours' ? byPhaseHours.get(p.id) || 0 : byPhaseDollars.get(p.id) || 0;
        return {
          label: p.name,
          planned: budget,
          actual: value,
          onClick: () => ctx.onDrill({ kind: 'budget-editor', phaseId: p.id }),
          ariaLabel: `Edit ${p.name} budget`,
        };
      })
      .filter((r) => r.actual > 0 || r.planned > 0);
  }, [phases, entries, budgets, ctx.members, ctx.billingRoles, mode, ctx.onDrill]);

  // Per-phase rows on the shared WList anatomy (multi-column at width).
  // The Meter's own mini-bar `rows` moved here (Decision 1) — a text pct
  // replaces the inline bar since WList's row anatomy is lead/primary/
  // meta/trailing, not a bar chart; the aggregate Meter above still shows
  // the bar for the project total.
  const phaseListRows: WListRow[] = useMemo(
    () =>
      phaseRows.map((r) => {
        const pct = r.planned > 0 ? Math.round((r.actual / r.planned) * 100) : 0;
        return {
          id: r.label,
          primary: r.label,
          meta: `${fmt(r.actual)} of ${fmt(r.planned)}`,
          trailing: (
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: pct > 100 ? 'var(--color-danger)' : 'var(--tm)',
              }}
            >
              {pct}%
            </span>
          ),
          onClick: r.onClick,
        };
      }),
    [phaseRows, fmt]
  );

  // 8-week burn — actual hours & dollars, by week.
  const weekly = useMemo(() => {
    const buckets: Record<string, { hours: number; dollars: number }> = {};
    for (let w = 0; w < 8; w++) {
      const ws = mondayOf(addDays(today, -7 * (7 - w)));
      buckets[ws] = { hours: 0, dollars: 0 };
    }
    for (const e of entries) {
      const ws = mondayOf(e.date);
      if (!(ws in buckets)) continue;
      const h = Number(e.hours) || 0;
      const m = ctx.members.find((x) => x.id === e.teamMemberId);
      buckets[ws].hours += h;
      buckets[ws].dollars += h * projectMemberRate(m, ctx.billingRoles);
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStart, d]) => ({
        label: shortWeek(weekStart),
        weekStart,
        hours: d.hours,
        dollars: d.dollars,
      }));
  }, [entries, today, ctx.members, ctx.billingRoles]);

  // Forecast-vs-actual variance, last 4 completed weeks (hours — forecasts
  // are logged in hours regardless of the mode toggle).
  const variance = useMemo(() => {
    let actual4 = 0;
    for (const e of entries)
      if (e.date >= past4Start && e.date <= past4End) actual4 += Number(e.hours) || 0;
    let forecast4 = 0;
    for (const f of forecasts) {
      const d = f.dayDate || f.weekStartDate;
      if (d >= past4Start && d <= past4End) forecast4 += Number(f.hours) || 0;
    }
    return { actual: actual4, forecast: forecast4, delta: actual4 - forecast4 };
  }, [entries, forecasts, past4Start, past4End]);

  const varianceDelta =
    variance.forecast > 0
      ? {
          value: `${variance.delta > 0 ? '+' : ''}${fmtHours(variance.delta)}`,
          direction: (variance.delta > 0 ? 'up' : variance.delta < 0 ? 'down' : 'flat') as
            | 'up'
            | 'down'
            | 'flat',
          tone: (Math.abs(variance.delta) / variance.forecast > 0.1 ? 'bad' : 'good') as
            | 'good'
            | 'bad',
        }
      : undefined;

  // Inline Set/Edit budget — Hours mode only (Dollars keeps its original
  // drill-only "Open budget editor" path, unchanged).
  const [showSetBudget, setShowSetBudget] = useState(false);
  const [hoursIn, setHoursIn] = useState('');
  const [saving, setSaving] = useState(false);

  async function saveHoursBudget() {
    setSaving(true);
    try {
      const existing = budgets.find((b) => !b.phaseId && !b.billingRoleId);
      const next: ProjectBudget = {
        id: existing?.id || '',
        projectId: ctx.project.id,
        budgetHours: hoursIn ? parseFloat(hoursIn) : existing?.budgetHours,
        budgetAmount: existing?.budgetAmount,
      };
      await ProjectBudgetService.save(next);
      setShowSetBudget(false);
      setHoursIn('');
      await reload();
    } finally {
      setSaving(false);
    }
  }

  // Profitability fold-in — cost is the SAME number as `actualDollars`
  // above (hours × rate, summed over the same `entries`); reused rather
  // than recomputed. Every function ported verbatim from the deleted
  // profitability-widget.tsx.
  const revenue = useMemo(
    () =>
      invoices
        .filter((i) => i.status === 'sent' || i.status === 'paid' || i.status === 'partial')
        .reduce((s, i) => s + (i.total || 0), 0),
    [invoices]
  );
  const cost = actualDollars;
  const profit = revenue - cost;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  const contractValue = ctx.project.contractValue || 0;
  const contractMargin = contractValue > 0 ? ((contractValue - cost) / contractValue) * 100 : null;
  const profitDelta =
    revenue > 0
      ? {
          value: `${fmtPct(margin)} margin`,
          direction: (profit < 0 ? 'down' : 'up') as 'down' | 'up',
          tone: (profit < 0 ? 'bad' : margin < 15 ? 'neutral' : 'good') as
            | 'bad'
            | 'neutral'
            | 'good',
        }
      : undefined;

  // Budget's own loading/error states still gate the whole card; "no
  // budget target set" no longer does (Profitability and the Contract/Fee
  // fields never depended on a budget target existing, so they must not
  // disappear just because Budget's summary has nothing to show yet).
  const isBudgetEmpty = totalBudget === 0 && !showSetBudget;
  const frameStatus = status === 'loading' ? 'loading' : status === 'error' ? 'error' : 'ready';

  const emptyAction =
    mode === 'hours'
      ? ctx.can.edit
        ? { label: '+ Set budget', onClick: () => setShowSetBudget(true) }
        : undefined
      : { label: 'Open budget editor', onClick: () => ctx.onDrill({ kind: 'budget-editor' }) };

  return (
    <WidgetFrame
      title="Budget"
      status={frameStatus}
      form="meter"
      error={{ message: 'Could not load budget.', onRetry: reload }}
      actions={<ModeToggle mode={mode} onChange={setMode} />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {showSetBudget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label
              htmlFor="sf-budget-hours-input"
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', fontWeight: 600 }}
            >
              Project total budget (hours)
            </label>
            <input
              id="sf-budget-hours-input"
              type="number"
              placeholder="800"
              value={hoursIn}
              onChange={(e) => setHoursIn(e.target.value)}
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSetBudget(false)} style={btnGhost}>
                Cancel
              </button>
              <button
                onClick={saveHoursBudget}
                disabled={saving || !hoursIn}
                style={{ ...btnGhost, opacity: !hoursIn ? 0.5 : 1 }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : isBudgetEmpty ? (
          <EmptyState compact title={`No ${mode} budget set.`} action={emptyAction} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <StatGroup>
              <Stat value={fmt(totalBudget)} label="Budget" />
              <Stat value={fmt(actual)} label="Actual" />
              <Stat value={fmt(remaining)} label="Remaining" />
              <Stat
                value={varianceDelta ? varianceDelta.value : '—'}
                label="Variance (4wk vs forecast)"
              />
            </StatGroup>
            <Meter
              label="Budget"
              planned={totalBudget}
              actual={actual}
              values={{
                planned: fmt(totalBudget),
                actual: fmt(actual),
                remaining: `${fmt(remaining)} left`,
              }}
            />
            {phaseListRows.length > 0 && <WList rows={phaseListRows} max={phaseListRows.length} />}
            <WChart
              kind="bar"
              data={weekly}
              series={[
                { key: mode === 'hours' ? 'hours' : 'dollars', label: 'Logged', tone: 'info' },
              ]}
              xKey="label"
              height={140}
              onBarClick={(_key, row) =>
                ctx.onDrill({ kind: 'time-entry-list', weekStart: row.weekStart })
              }
              tooltipFormatter={(v) => [mode === 'hours' ? `${v}h` : fmtMoney(v), 'Logged']}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' }}>
              {mode === 'hours' && ctx.can.edit && (
                <button onClick={() => setShowSetBudget(true)} style={linkBtn}>
                  Edit budget
                </button>
              )}
              <button onClick={() => ctx.onDrill({ kind: 'budget-editor' })} style={linkBtn}>
                Open budget editor
              </button>
            </div>
          </div>
        )}

        <Fields fields={budgetFields} />

        <div>
          <div style={sectionLabel}>Profitability</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Meter
              label="Revenue vs cost"
              planned={revenue || 1}
              actual={cost}
              values={{
                planned: fmtMoney(revenue),
                actual: fmtMoney(cost),
                remaining: contractMargin !== null ? `${fmtPct(contractMargin)} proj.` : '',
              }}
              rows={[
                { label: 'Revenue', planned: revenue || 1, actual: revenue },
                { label: 'Cost', planned: revenue || 1, actual: cost },
                ...(contractValue > 0
                  ? [{ label: 'Contract', planned: contractValue, actual: cost }]
                  : []),
              ]}
            />
            <Stat
              value={fmtMoney(profit)}
              label="net profit"
              delta={profitDelta}
              onClick={() => ctx.onDrill({ kind: 'invoice-list' })}
            />
          </div>
        </div>
      </div>
    </WidgetFrame>
  );
}

export const budgetWidget: ProjectWidgetDef = {
  id: 'budget',
  title: 'Budget',
  description:
    'Hours or dollars budget vs actual, with per-phase breakdown, 8-week burn, and forecast accuracy. Merges Hours/Cost Budget vs Actual, Burn Rate, and Forecast vs Actual.',
  category: 'financial',
  requires: ['projects.view_financials'],
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  inputSurface: true,
  render: (ctx, _cols, slot) => <BudgetBody ctx={ctx} slot={slot as WidgetSlot} />,
};
